'use client';

import { c as _c } from "react/compiler-runtime";
import type { RefObject } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AnimatedOutcomePercent } from '@/components/markets/animated-outcome-percent';
import { outcomeLabelTooltipArrowClassName, outcomeLabelTooltipClassName, outcomeLabelTooltipContentStyle, useOutcomeLabelTruncation } from '@/components/markets/outcome-label-tooltip';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { DrainedChip } from '@/components/ui/drained-chip';
import { Icon } from '@/components/ui/icon';
import { Award01IconSolid, Tick02Icon } from '@/components/ui/icon/glyphs';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import { cn } from '@/lib/ui/utils';
export type OutcomeCardVariant = 'interactive' | 'static' | 'winner';
export interface OutcomeCardProps {
  /** Stable parent entity address used to scope the outcome image identity. */
  entityPubkey: string;
  index: number;
  label: string;
  /** Already-computed percent (0-100). The card never derives it from trading state. */
  percent: number;
  /** Consensus goal percentage shown after the current outcome percentage. */
  goalPercent?: number | undefined;
  /** Hide the percentage entirely — used by the winner when consensus is unparseable. */
  showPercent?: boolean;
  /** Render the percentage with two decimals (live trading view). Resolved views pass `false`. */
  decimals?: boolean;
  imageUrl?: string | null;
  imageNsfw?: boolean;
  /** Accessible name for the image. Defaults to empty (decorative) — the
   *  winner passes a descriptive alt for a11y (FR-021). */
  imageAlt?: string;
  /**
   * Enable the click-to-zoom lightbox preview on the outcome image. Default
   * `false`. For the interactive variant the image becomes its own preview
   * control (its own button) while the rest of the card stays the select
   * target — achieved with the overlay-select layout below, so the two
   * controls never nest as buttons.
   */
  imagePreview?: boolean;
  ticker?: string | null;
  variant: OutcomeCardVariant;
  /** Interactive only — current selection state (drives `aria-pressed`). */
  isSelected?: boolean;
  /** Interactive only — selecting the outcome. When omitted the card is inert. */
  onSelect?: ((index: number) => void) | undefined;
  /** Interactive only — accessible name for the selection control. */
  ariaLabel?: string;
  /** Live view only — category emoji shown when the outcome has no image. */
  fallbackEmoji?: string;
  /** Static (loser) only — render the `Drained` chip for the active wallet's dead position. */
  drained?: boolean;
  /** Already-translated `Drained` label. */
  drainedLabel?: string;
  /** Winner only — already-translated "Winner" label. */
  winnerLabel?: string;
  /** Winner only — already-translated caption under the percent (e.g. "Final consensus"). */
  caption?: string;
  /** Renderer-owned display leaf; bypasses AnimatedOutcomePercent. */
  percentContent?: React.ReactNode;
  /** Renderer-owned goal leaf. */
  goalContent?: React.ReactNode;
  selectionControlRef?: RefObject<HTMLButtonElement | null>;
}

/**
 * Shared outcome card — the single visual for one market outcome, used by the
 * live Outcomes panel (interactive) and the Resolved panel (static losers +
 * elevated winner). The percentage is always an input prop so the same card
 * renders live implied odds or a settled final share without any internal data
 * dependency. See spec 109.
 */
export function OutcomeCard(props) {
  const $ = _c(145);
  const {
    entityPubkey,
    index,
    label,
    percent,
    goalPercent,
    showPercent: t0,
    decimals: t1,
    imageUrl: t2,
    imageNsfw: t3,
    imageAlt,
    imagePreview: t4,
    ticker: t5,
    variant,
    isSelected: t6,
    onSelect,
    ariaLabel,
    fallbackEmoji,
    drained: t7,
    drainedLabel,
    winnerLabel,
    caption,
    percentContent,
    goalContent,
    selectionControlRef
  } = props;
  const showPercent = t0 === undefined ? true : t0;
  const decimals = t1 === undefined ? true : t1;
  const imageUrl = t2 === undefined ? null : t2;
  const imageNsfw = t3 === undefined ? false : t3;
  const imagePreview = t4 === undefined ? false : t4;
  const ticker = t5 === undefined ? null : t5;
  const isSelected = t6 === undefined ? false : t6;
  const drained = t7 === undefined ? false : t7;
  let t8;
  if ($[0] !== index || $[1] !== label) {
    t8 = {
      title: label,
      index
    };
    $[0] = index;
    $[1] = label;
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  let t9;
  if ($[3] !== entityPubkey) {
    t9 = {
      marketPubkey: entityPubkey
    };
    $[3] = entityPubkey;
    $[4] = t9;
  } else {
    t9 = $[4];
  }
  const colors = useOutcomeColor(t8, t9);
  const {
    setLabelSpanEl,
    isTruncated
  } = useOutcomeLabelTruncation(label);
  let t10;
  if ($[5] !== goalPercent) {
    t10 = goalPercent !== undefined && Number.isFinite(goalPercent) && goalPercent > 0 ? `/${String(Math.round(goalPercent))}%` : null;
    $[5] = goalPercent;
    $[6] = t10;
  } else {
    t10 = $[6];
  }
  const goalLabel = t10;
  const hasImage = imageUrl !== null && imageUrl !== "";
  const showEmoji = !hasImage && fallbackEmoji !== undefined;
  let t11;
  if ($[7] !== colors.cta || $[8] !== colors.hue || $[9] !== entityPubkey || $[10] !== fallbackEmoji || $[11] !== imageAlt || $[12] !== imageNsfw || $[13] !== imagePreview || $[14] !== imageUrl || $[15] !== index || $[16] !== showEmoji) {
    t11 = (sizeClass, rounded) => showEmoji ? <span className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", sizeClass, rounded, colors.cta)}><span aria-hidden={true} className="flex size-full items-center justify-center text-xl leading-none">{fallbackEmoji}</span></span> : <span className={cn("relative flex shrink-0 items-center justify-center overflow-hidden", sizeClass, rounded, colors.cta, imagePreview && "pointer-events-auto")}><OptimizedImage variant="thumbnail" entity={{
        kind: "outcome",
        marketPubkey: entityPubkey,
        outcomeIndex: index,
        hue: colors.hue,
        imageUrl: imageUrl ?? null,
        nsfw: imageNsfw
      }} alt={imageAlt ?? ""} size={sizeClass} rounded={rounded} preview={imagePreview} loadReveal="none" className="size-full object-cover" /></span>;
    $[7] = colors.cta;
    $[8] = colors.hue;
    $[9] = entityPubkey;
    $[10] = fallbackEmoji;
    $[11] = imageAlt;
    $[12] = imageNsfw;
    $[13] = imagePreview;
    $[14] = imageUrl;
    $[15] = index;
    $[16] = showEmoji;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const mediaBlock = t11;
  let t12;
  if ($[18] !== colors.text || $[19] !== ticker) {
    t12 = ticker !== null ? <span className={cn("shrink-0 rounded-item bg-background-tertiary px-1.5 py-0.5 font-mono text-caption font-medium tracking-wide", colors.text)}>{ticker}</span> : null;
    $[18] = colors.text;
    $[19] = ticker;
    $[20] = t12;
  } else {
    t12 = $[20];
  }
  const tickerChip = t12;
  if (variant === "winner") {
    let t13;
    if ($[21] !== colors.outline) {
      t13 = cn("flex w-full flex-col gap-3 rounded-panel-row border border-transparent p-3", colors.outline);
      $[21] = colors.outline;
      $[22] = t13;
    } else {
      t13 = $[22];
    }
    let t14;
    if ($[23] !== colors.text) {
      t14 = cn("size-4", colors.text);
      $[23] = colors.text;
      $[24] = t14;
    } else {
      t14 = $[24];
    }
    let t15;
    if ($[25] !== t14) {
      t15 = <Icon icon={Award01IconSolid} aria-hidden={true} className={t14} />;
      $[25] = t14;
      $[26] = t15;
    } else {
      t15 = $[26];
    }
    let t16;
    if ($[27] !== colors.text || $[28] !== winnerLabel) {
      t16 = winnerLabel !== undefined && <span className={cn("text-caption font-medium uppercase tracking-wide", colors.text)}>{winnerLabel}</span>;
      $[27] = colors.text;
      $[28] = winnerLabel;
      $[29] = t16;
    } else {
      t16 = $[29];
    }
    let t17;
    if ($[30] !== t15 || $[31] !== t16) {
      t17 = <div className="flex items-center gap-1.5">{t15}{t16}</div>;
      $[30] = t15;
      $[31] = t16;
      $[32] = t17;
    } else {
      t17 = $[32];
    }
    let t18;
    if ($[33] !== mediaBlock) {
      t18 = mediaBlock("size-12", "rounded-panel-inset");
      $[33] = mediaBlock;
      $[34] = t18;
    } else {
      t18 = $[34];
    }
    let t19;
    if ($[35] !== colors.text) {
      t19 = cn("truncate text-body-lg font-medium", colors.text);
      $[35] = colors.text;
      $[36] = t19;
    } else {
      t19 = $[36];
    }
    let t20;
    if ($[37] !== label || $[38] !== setLabelSpanEl || $[39] !== t19) {
      t20 = <span ref={setLabelSpanEl} className={t19}>{label}</span>;
      $[37] = label;
      $[38] = setLabelSpanEl;
      $[39] = t19;
      $[40] = t20;
    } else {
      t20 = $[40];
    }
    let t21;
    if ($[41] !== t20 || $[42] !== tickerChip) {
      t21 = <div className="flex min-w-0 flex-1 flex-col gap-1">{t20}{tickerChip}</div>;
      $[41] = t20;
      $[42] = tickerChip;
      $[43] = t21;
    } else {
      t21 = $[43];
    }
    let t22;
    if ($[44] !== colors.text || $[45] !== decimals || $[46] !== percent || $[47] !== showPercent) {
      t22 = showPercent && <AnimatedOutcomePercent value={percent} decimals={decimals} animateOnMount={true} className={cn("text-heading-md font-bold tabular-nums", colors.text)} />;
      $[44] = colors.text;
      $[45] = decimals;
      $[46] = percent;
      $[47] = showPercent;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    let t23;
    if ($[49] !== caption || $[50] !== colors.text) {
      t23 = caption !== undefined && <span className={cn("text-micro uppercase tracking-wide", colors.text)}>{caption}</span>;
      $[49] = caption;
      $[50] = colors.text;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    let t24;
    if ($[52] !== t22 || $[53] !== t23) {
      t24 = <div className="flex shrink-0 flex-col items-end">{t22}{t23}</div>;
      $[52] = t22;
      $[53] = t23;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    let t25;
    if ($[55] !== t18 || $[56] !== t21 || $[57] !== t24) {
      t25 = <div className="flex items-center gap-3">{t18}{t21}{t24}</div>;
      $[55] = t18;
      $[56] = t21;
      $[57] = t24;
      $[58] = t25;
    } else {
      t25 = $[58];
    }
    let t26;
    if ($[59] !== t13 || $[60] !== t17 || $[61] !== t25) {
      t26 = <div data-testid="outcome-card" data-variant="winner" className={t13}>{t17}{t25}</div>;
      $[59] = t13;
      $[60] = t17;
      $[61] = t25;
      $[62] = t26;
    } else {
      t26 = $[62];
    }
    const winnerInner = t26;
    const t27 = !isTruncated;
    let t28;
    if ($[63] !== colors) {
      t28 = outcomeLabelTooltipClassName(colors);
      $[63] = colors;
      $[64] = t28;
    } else {
      t28 = $[64];
    }
    let t29;
    if ($[65] !== colors) {
      t29 = outcomeLabelTooltipContentStyle(colors);
      $[65] = colors;
      $[66] = t29;
    } else {
      t29 = $[66];
    }
    let t30;
    if ($[67] !== colors) {
      t30 = outcomeLabelTooltipArrowClassName(colors);
      $[67] = colors;
      $[68] = t30;
    } else {
      t30 = $[68];
    }
    let t31;
    if ($[69] !== label || $[70] !== t27 || $[71] !== t28 || $[72] !== t29 || $[73] !== t30 || $[74] !== winnerInner) {
      t31 = <AppTooltip content={label} disabled={t27} side="bottom" align="center" delayDuration={100} contentClassName={t28} contentStyle={t29} arrowClassName={t30}>{winnerInner}</AppTooltip>;
      $[69] = label;
      $[70] = t27;
      $[71] = t28;
      $[72] = t29;
      $[73] = t30;
      $[74] = winnerInner;
      $[75] = t31;
    } else {
      t31 = $[75];
    }
    return t31;
  }
  let t13;
  if ($[76] !== ariaLabel || $[77] !== colors.fill || $[78] !== colors.hue || $[79] !== colors.outline || $[80] !== colors.text || $[81] !== decimals || $[82] !== drained || $[83] !== drainedLabel || $[84] !== goalContent || $[85] !== goalLabel || $[86] !== index || $[87] !== isSelected || $[88] !== label || $[89] !== mediaBlock || $[90] !== onSelect || $[91] !== percent || $[92] !== percentContent || $[93] !== selectionControlRef || $[94] !== setLabelSpanEl || $[95] !== showPercent || $[96] !== tickerChip || $[97] !== variant) {
    const surfaceClass = cn("relative box-border flex min-w-0 w-full flex-row items-center gap-2 rounded-overlay border-solid [border-width:var(--border-width-hairline)] border-transparent p-2 text-left", variant === "interactive" ? colors.fill : colors.outline, variant === "interactive" && isSelected && `border-outcome-${colors.hue}-text`);
    let t14;
    if ($[99] !== mediaBlock) {
      t14 = mediaBlock("size-10", "rounded-item");
      $[99] = mediaBlock;
      $[100] = t14;
    } else {
      t14 = $[100];
    }
    let t15;
    if ($[101] !== colors.text || $[102] !== isSelected || $[103] !== variant) {
      t15 = variant === "interactive" && isSelected && <span aria-hidden={true} data-testid="outcome-card-selected-icon" className="pointer-events-none absolute -right-1 -bottom-1 flex size-4 items-center justify-center"><span data-testid="outcome-card-selected-icon-background" className="absolute inset-0 rounded-full bg-background-primary/50 backdrop-blur-sm" /><Icon icon={Tick02Icon} className={cn("relative size-3", colors.text)} /></span>;
      $[101] = colors.text;
      $[102] = isSelected;
      $[103] = variant;
      $[104] = t15;
    } else {
      t15 = $[104];
    }
    let t16;
    if ($[105] !== t14 || $[106] !== t15) {
      t16 = <span className="relative shrink-0">{t14}{t15}</span>;
      $[105] = t14;
      $[106] = t15;
      $[107] = t16;
    } else {
      t16 = $[107];
    }
    const compactMediaBlock = t16;
    let t17;
    if ($[108] !== colors.text) {
      t17 = cn("min-w-0 truncate text-body font-medium tracking-tight", colors.text);
      $[108] = colors.text;
      $[109] = t17;
    } else {
      t17 = $[109];
    }
    let t18;
    if ($[110] !== label || $[111] !== setLabelSpanEl || $[112] !== t17) {
      t18 = <span ref={setLabelSpanEl} className={t17}>{label}</span>;
      $[110] = label;
      $[111] = setLabelSpanEl;
      $[112] = t17;
      $[113] = t18;
    } else {
      t18 = $[113];
    }
    let t19;
    if ($[114] !== drained || $[115] !== drainedLabel) {
      t19 = drained && drainedLabel !== undefined && <DrainedChip label={drainedLabel} className="shrink-0" />;
      $[114] = drained;
      $[115] = drainedLabel;
      $[116] = t19;
    } else {
      t19 = $[116];
    }
    let t20;
    if ($[117] !== t18 || $[118] !== t19 || $[119] !== tickerChip) {
      t20 = <span className="flex w-full min-w-0 items-center gap-1.5">{t18}{tickerChip}{t19}</span>;
      $[117] = t18;
      $[118] = t19;
      $[119] = tickerChip;
      $[120] = t20;
    } else {
      t20 = $[120];
    }
    let t21;
    if ($[121] !== colors.text || $[122] !== decimals || $[123] !== goalContent || $[124] !== goalLabel || $[125] !== percent || $[126] !== percentContent || $[127] !== showPercent) {
      t21 = showPercent && <span className={cn("flex shrink-0 items-baseline gap-1", colors.text)}>{percentContent ?? <AnimatedOutcomePercent value={percent} decimals={decimals} animateOnMount={true} className={cn("shrink-0 text-body-lg font-medium tabular-nums tracking-tight", colors.text)} />}{goalContent ?? (goalLabel !== null && <span className={cn("text-caption font-medium tabular-nums", colors.text)}>{goalLabel}</span>)}</span>;
      $[121] = colors.text;
      $[122] = decimals;
      $[123] = goalContent;
      $[124] = goalLabel;
      $[125] = percent;
      $[126] = percentContent;
      $[127] = showPercent;
      $[128] = t21;
    } else {
      t21 = $[128];
    }
    let t22;
    if ($[129] !== t20 || $[130] !== t21) {
      t22 = <span className="flex min-h-0 min-w-0 flex-1 flex-col items-start justify-center gap-0 overflow-hidden leading-5">{t20}{t21}</span>;
      $[129] = t20;
      $[130] = t21;
      $[131] = t22;
    } else {
      t22 = $[131];
    }
    const textStack = t22;
    t13 = variant === "interactive" && onSelect !== undefined ? <div className={cn(surfaceClass, "cursor-pointer")} data-testid="outcome-card" data-variant="interactive"><button ref={selectionControlRef} type="button" className={cn("absolute inset-0 cursor-pointer rounded-overlay border-solid [border-width:var(--border-width-hairline)] border-transparent outline-none", `focus-visible:border-outcome-${colors.hue}-text`)} onClick={() => {
        onSelect(index);
      }} aria-label={ariaLabel} aria-pressed={isSelected} /><span className="pointer-events-none relative z-10 flex min-w-0 flex-1 flex-row items-center gap-2">{compactMediaBlock}{textStack}</span></div> : <div className={surfaceClass} data-testid="outcome-card" data-variant="static">{compactMediaBlock}{textStack}</div>;
    $[76] = ariaLabel;
    $[77] = colors.fill;
    $[78] = colors.hue;
    $[79] = colors.outline;
    $[80] = colors.text;
    $[81] = decimals;
    $[82] = drained;
    $[83] = drainedLabel;
    $[84] = goalContent;
    $[85] = goalLabel;
    $[86] = index;
    $[87] = isSelected;
    $[88] = label;
    $[89] = mediaBlock;
    $[90] = onSelect;
    $[91] = percent;
    $[92] = percentContent;
    $[93] = selectionControlRef;
    $[94] = setLabelSpanEl;
    $[95] = showPercent;
    $[96] = tickerChip;
    $[97] = variant;
    $[98] = t13;
  } else {
    t13 = $[98];
  }
  const chip = t13;
  const t14 = !isTruncated;
  let t15;
  if ($[132] !== colors) {
    t15 = outcomeLabelTooltipClassName(colors);
    $[132] = colors;
    $[133] = t15;
  } else {
    t15 = $[133];
  }
  let t16;
  if ($[134] !== colors) {
    t16 = outcomeLabelTooltipContentStyle(colors);
    $[134] = colors;
    $[135] = t16;
  } else {
    t16 = $[135];
  }
  let t17;
  if ($[136] !== colors) {
    t17 = outcomeLabelTooltipArrowClassName(colors);
    $[136] = colors;
    $[137] = t17;
  } else {
    t17 = $[137];
  }
  let t18;
  if ($[138] !== chip || $[139] !== label || $[140] !== t14 || $[141] !== t15 || $[142] !== t16 || $[143] !== t17) {
    t18 = <AppTooltip content={label} disabled={t14} side="bottom" align="center" delayDuration={100} contentClassName={t15} contentStyle={t16} arrowClassName={t17}>{chip}</AppTooltip>;
    $[138] = chip;
    $[139] = label;
    $[140] = t14;
    $[141] = t15;
    $[142] = t16;
    $[143] = t17;
    $[144] = t18;
  } else {
    t18 = $[144];
  }
  return t18;
}